import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { checkAdminClaims } from '../../utils/adminClaims';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faTimesCircle,
  faEye,
  faSearch,
  faHourglassHalf,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';
import {
  getApplicationsList,
  updateApplicationStatus,
  ApplicationItem
} from '../../services/applicationService';
import { getEventById } from '../../services/eventService';
import { EventItem } from '../../types/event';
import { logger } from '../../utils/logger';
import ApplicationDetailsModal from './ApplicationDetailsModal';
import '../../styles/ApplicationsDashboard.css';
import '../../styles/BackButton.css';
import '../../styles/EventBadge.css';
import '../../styles/ApplicationActionPanel.css';

interface ApplicationsDashboardProps {
  eventId?: string;
  showBackButton?: boolean;
  onBack?: () => void;
}

const ApplicationsDashboard = ({ 
  eventId, 
  showBackButton = false,
  onBack 
}: ApplicationsDashboardProps): React.ReactElement => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState<ApplicationItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'approved' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [viewApplication, setViewApplication] = useState<ApplicationItem | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string>('');
  const [processingId, setProcessingId] = useState<string | null>(null);
  const [event, setEvent] = useState<EventItem | null>(null);
  const [loadingEvent, setLoadingEvent] = useState<boolean>(false);
  const [useStandardMessage, setUseStandardMessage] = useState<boolean>(false);
  const [standardMessages] = useState<string[]>([
    "We cannot provide any further reason",
    "The party has reached its limit",
    "Your profile does not match our current event requirements",
    "We recommend applying for future events"
  ]);
  const [selectedStandardMessage, setSelectedStandardMessage] = useState<string>("");


  const { currentUser, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  // Fetch applications from API (admin only)
  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!currentUser) throw new Error('Not authenticated');
      const data = await getApplicationsList(eventId);
      // Always filter by eventId on the client as a safeguard, even if backend is correct
      const filtered = eventId ? data.filter(app => app.eventId === eventId) : data;
      setApplications(filtered);
      if (eventId && filtered.length === 0) {
        setError('No applications found for this event.');
      }
    } catch (err) {
      logger.error('❌ Error fetching applications:', err);
      setError('Failed to load applications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [currentUser, eventId, setLoading, setError, setApplications]);

  // Fetch event details by ID
  const fetchEventDetails = async (id: string) => {
    setLoadingEvent(true);
    try {
      logger.info('🔍 Fetching event details', { eventId: id });
      const eventData = await getEventById(id);
      if (eventData) {
        setEvent(eventData);
        logger.success('✅ Event details fetched successfully', { title: eventData.title });
      } else {
        logger.warn('⚠️ Event not found', { eventId: id });
      }
    } catch (err) {
      logger.error('❌ Error fetching event details:', err);
      // No need to set error message here as we're focused on applications
    } finally {
      setLoadingEvent(false);
    }
  };

  // Check admin status on mount or user change
  useEffect(() => {
    if (currentUser) {
      checkAdminClaims(currentUser).then(setIsAdmin);
    } else {
      setIsAdmin(false);
    }
  }, [currentUser]);

  // Fetch applications on component mount (only if admin)
  useEffect(() => {
    if (!authLoading && isAdmin) {
      fetchApplications();
      if (eventId) {
        fetchEventDetails(eventId);
      }
    }
  }, [eventId, isAdmin, authLoading, fetchApplications]);

  // Filter applications based on current filter and search query
  const filteredApplications = applications.filter(app => {
    const matchesFilter = filter === 'all' || app.status === filter;
    const matchesSearch = searchQuery === '' ||
      app.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      app.id.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesFilter && matchesSearch;
  });

  // Format date
  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Handle back navigation to Events Dashboard
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/admin');
    }
  };

  // Handle application approval
  const handleApprove = async (application: ApplicationItem) => {
    setProcessingId(application.id);

    try {
      logger.info('✅ Approving application:', { id: application.id });
      await updateApplicationStatus(application.id, 'approved');

      // Fetch updated applications to ensure rejectionReason is properly cleared
      fetchApplications();

      // Close the modal after successful update
      setViewApplication(null);

      logger.success('✅ Application approved successfully');
    } catch (err) {
      logger.error('❌ Error approving application:', err);
      setError('Failed to approve application. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };

  // Handle application rejection
  const handleReject = async (application: ApplicationItem) => {
    const finalRejectionReason = useStandardMessage ? selectedStandardMessage : rejectionReason;
    
    // Validate: require a new rejection reason when changing from approved to rejected
    if (application.status === 'approved' && !finalRejectionReason) {
      setError('Please provide a rejection reason when rejecting a previously approved application.');
      return;
    }
    
    setProcessingId(application.id);

    try {
      logger.info('❌ Rejecting application:', { 
        id: application.id, 
        previousStatus: application.status,
        reason: finalRejectionReason || 'No reason provided',
        isStandardMessage: useStandardMessage
      });
      
      await updateApplicationStatus(application.id, 'rejected', finalRejectionReason);

      // Reset form and refresh the applications list
      setRejectionReason('');
      setUseStandardMessage(false);
      setSelectedStandardMessage('');
      setViewApplication(null);
      fetchApplications();
    } catch (err) {
      logger.error('❌ Error rejecting application:', err);
      setError('Failed to reject application. Please try again.');
    } finally {
      setProcessingId(null);
    }
  };



  // Render status badge
  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="status-badge pending"><FontAwesomeIcon icon={faHourglassHalf} /> Pending</span>;
      case 'approved':
        return <span className="status-badge approved"><FontAwesomeIcon icon={faCheckCircle} /> Approved</span>;
      case 'rejected':
        return <span className="status-badge rejected"><FontAwesomeIcon icon={faTimesCircle} /> Rejected</span>;
      default:
        return <span className="status-badge">{status}</span>;
    }
  };

  // Helper function to format name display
  const formatNameDisplay = (application: ApplicationItem) => {
    if (application.attendeeType === 'single') {
      return application.name;
    }
    // For couples, show name & partner alias with ellipsis if too long
    const fullName = `${application.name} & ${application.partnerAlias}`;
    return (
      <span
        className="couple-names"
        title={fullName}
        style={{
          cursor: 'help',
          borderBottom: '1px dotted rgba(255,255,255,0.5)'
        }}
      >
        {fullName.length > 30 ? `${fullName.substring(0, 27)}...` : fullName}
      </span>
    );
  };

  // Handle standard message checkbox change
  const handleUseStandardMessageChange = (useStandard: boolean) => {
    setUseStandardMessage(useStandard);
    if (useStandard && standardMessages.length > 0) {
      setSelectedStandardMessage(standardMessages[0]);
    }
  };

  // Render application modal and its action panel
  const renderApplicationModal = () => {
    if (!viewApplication) return null;

    return (      <ApplicationDetailsModal
        application={viewApplication}
        onClose={() => setViewApplication(null)}
        renderStatusBadge={renderStatusBadge}
        formatDate={formatDate}
        processingId={processingId}
        rejectionReason={rejectionReason}
        onReasonChange={setRejectionReason}
        onApprove={handleApprove}
        onReject={handleReject}
        useStandardMessage={useStandardMessage}
        onUseStandardMessageChange={handleUseStandardMessageChange}
        standardMessages={standardMessages}
        selectedStandardMessage={selectedStandardMessage}
        onStandardMessageSelect={setSelectedStandardMessage}
      />
    );
  };

  if (!isAdmin && !authLoading) {
    return (
      <div className="applications-dashboard">
        <div className="error-message">You do not have admin access to view applications.</div>
      </div>
    );
  }

  return (
    <div className="applications-dashboard">
      <div className="dashboard-header">
        {showBackButton && (
          <button onClick={handleBack} className="back-button">
            <FontAwesomeIcon icon={faArrowLeft} /> Back to Events
          </button>
        )}
        <h1>
          {eventId ? (
            loadingEvent ? (
              'Loading Event...'
            ) : (
              event ? `Applications for ${event.title}` : 'Event Applications'
            )
          ) : (
            'Applications Dashboard'
          )}
        </h1>
      </div>

      <div className="dashboard-toolbar">
        <div className="filter-toolbar">
          <button
            className={`filter-button ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-button ${filter === 'pending' ? 'active' : ''}`}
            onClick={() => setFilter('pending')}
          >
            Pending
          </button>
          <button
            className={`filter-button ${filter === 'approved' ? 'active' : ''}`}
            onClick={() => setFilter('approved')}
          >
            Approved
          </button>
          <button
            className={`filter-button ${filter === 'rejected' ? 'active' : ''}`}
            onClick={() => setFilter('rejected')}
          >
            Rejected
          </button>
        </div>

        <div className="search-container">
          <FontAwesomeIcon icon={faSearch} className="search-icon" />
          <input
            type="text"
            placeholder="Search by name, email, or ID"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>

      </div>

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="applications-list">
        {loading ? (
          <div className="loading-indicator">
            <div className="spinner"></div>
            <p>Loading applications...</p>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="no-applications">
            <p>No applications found matching your criteria.</p>
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <table className="applications-table desktop-only">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Photo</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Type</th>
                  <th>Submitted</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.map(application => (
                  <tr key={application.id}>
                    <td className="status-cell" data-label="Status">{renderStatusBadge(application.status)}</td>
                    <td className="image-cell" data-label="Photo">
                      {isHttpUrl(application.photoUrl) ? (
                        <img src={application.photoUrl} alt="Applicant" className="application-thumbnail" />
                      ) : (
                        <div className="application-thumbnail placeholder" title="No photo available" aria-label="No photo">
                          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display:'block',margin:'auto'}}>
                            <rect x="3" y="3" width="18" height="18" rx="4" fill="#444"/>
                            <path d="M7 17L10.5 13.5L13.5 16.5L17 12" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            <circle cx="8.5" cy="8.5" r="1.5" fill="#888"/>
                          </svg>
                        </div>
                      )}
                    </td>
                    <td data-label="Name"><span className="application-name-desktop">{formatNameDisplay(application)}</span></td>
                    <td data-label="Email">{application.email}</td>
                    <td data-label="Type">{application.attendeeType === 'single' ? 'Single' : 'Couple'}</td>
                    <td data-label="Submitted">{formatDate(application.submittedAt)}</td>
                    <td data-label="Actions">
                      <button
                        className="view-button"
                        onClick={() => {
                          // Clear the rejection reason when viewing an application
                          setRejectionReason('');
                          setUseStandardMessage(false);
                          setSelectedStandardMessage('');
                          setViewApplication(application);
                        }}
                      >
                        <FontAwesomeIcon icon={faEye} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Mobile Card Layout */}
            <div className="applications-cards mobile-only">
              {filteredApplications.map(application => (
                <div className="application-card" key={application.id}>
                  <div className="application-card-photo-wrapper">
                    {isHttpUrl(application.photoUrl) ? (
                      <img src={application.photoUrl} alt="Applicant" className="application-card-photo" />
                    ) : (
                      <div className="application-card-photo application-card-photo-placeholder" title="No photo available" aria-label="No photo">
                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{display:'block',margin:'auto'}}>
                          <rect x="3" y="3" width="18" height="18" rx="4" fill="#444"/>
                          <path d="M7 17L10.5 13.5L13.5 16.5L17 12" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <circle cx="8.5" cy="8.5" r="1.5" fill="#888"/>
                        </svg>
                      </div>
                    )}
                    <div className="application-card-status-badge">
                      {renderStatusBadge(application.status)}
                    </div>
                  </div>
                  <div className="application-card-name">{formatNameDisplay(application)}</div>
                  <div className="application-card-info-table">
                    <div className="application-card-info-row"><span className="application-card-info-label">Email</span><span>{application.email}</span></div>
                    <div className="application-card-info-row"><span className="application-card-info-label">Type</span><span>{application.attendeeType === 'single' ? 'Single' : 'Couple'}</span></div>
                    <div className="application-card-info-row"><span className="application-card-info-label">Submitted</span><span>{formatDate(application.submittedAt)}</span></div>
                  </div>
                  <div className="application-card-action">
                    <button
                      className="view-button"
                      onClick={() => {
                        // Clear the rejection reason when viewing an application
                        setRejectionReason('');
                        setUseStandardMessage(false);
                        setSelectedStandardMessage('');
                        setViewApplication(application);
                      }}
                    >
                      <FontAwesomeIcon icon={faEye} /> View
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {viewApplication && renderApplicationModal()}
    </div>
  );
};

// Utility to check if a URL is a real HTTP(S) URL
function isHttpUrl(url?: string): boolean {
  return typeof url === 'string' && /^https?:\/\//.test(url);
}

export default ApplicationsDashboard;
